import React, { FC, useState } from "react";
import {
  IAvaQuestTree,
  IQuestResposta,
  ISistemaAvaliacao,
  ITreeQuestPerGru,
} from "@deltasge/marauders-map";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
} from "@mui/material";
import { Avatar } from "components/Avatar";
import { AccordionAtitudeComponent } from "./AccordionAtitudeComponent";
import { AccordionLegendaComponent } from "./AccordionLegendaComponent";
import { PerGruComponent } from "./PerGruComponent";
import { LoadingButton } from "@mui/lab";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { api, history } from "configs";
import { useLocation } from "react-router-dom";

const setRespostaOnTree = ({
  index,
  resposta,
  arr,
  idsGrupos,
}: {
  index: number;
  resposta: IQuestResposta;
  arr: ITreeQuestPerGru[];
  idsGrupos: number[];
}) => {
  if (idsGrupos.length == 0) {
    arr[index].resposta = resposta;
  } else {
    const [first, ...rest] = idsGrupos;
    const indexGrupo = arr.findIndex((f) => f.grupo?.id == first);
    const grupo = arr.at(indexGrupo)?.grupo;
    if (!grupo || !grupo.itens) {
      throw new Error("Grupo não encontrado");
    }
    grupo.itens = setRespostaOnTree({
      arr: grupo.itens,
      index,
      idsGrupos: rest,
      resposta,
    });
    arr[indexGrupo].grupo = grupo;
  }
  return [...arr];
};

const getRespostas = (arr: ITreeQuestPerGru[]): IQuestResposta[] => {
  const respostas: IQuestResposta[] = [];
  for (let index = 0; index < arr.length; index++) {
    const { grupo, resposta } = arr[index];
    if (resposta) respostas.push(resposta);
    if (grupo?.itens) {
      respostas.push(...getRespostas(grupo.itens));
    }
  }
  return respostas;
};

export const Avaliacao: FC<{
  avaliacao: IAvaQuestTree;
  sistemaAvaliacao: ISistemaAvaliacao;
}> = ({
  avaliacao: { avaliacao, legendas, atitudes, respostas },
  sistemaAvaliacao,
}) => {
  const { state } = useLocation<{ pathname?: string; search?: string }>();

  const [tree, setTree] = useState(respostas);
  const [saving, setSaving] = useState(false);

  let nomeAluno = avaliacao.aluno.nome;
  if (avaliacao.aluno.nomeusual?.trim()) {
    nomeAluno = avaliacao.aluno.nomeusual.trim();
  }

  let qtdeEtapas = 1;
  if (avaliacao.questionario.multiBimestre) {
    qtdeEtapas = sistemaAvaliacao.numeroBim.valueOf();
  }

  if (!avaliacao.id) {
    return null;
  }

  const setResposta = (args: {
    idsGrupos: number[];
    resposta: IQuestResposta;
    index: number;
  }) => {
    setTree(setRespostaOnTree({ ...args, arr: tree }));
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const respostas = getRespostas(tree);
      await api.post("/questionario-resposta/lote/", respostas);
      snack.success("Avaliação salva com sucesso!");
      if (state && state.pathname) {
        history.push(`${state.pathname}${state.search}`);
      } else {
        history.push("/questionario/avaliacoes");
      }
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleCancelar = () => {
    if (state && state.pathname) {
      history.push(`${state.pathname}${state.search}`);
    } else {
      history.push("/questionario/avaliacoes");
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardHeader
            avatar={
              <Avatar
                displayName={nomeAluno.rUpper()}
                src={avaliacao.aluno.imagemAluno?.caminhoArquivo}
              />
            }
            title={nomeAluno}
            subheader={avaliacao.turma.tituloTurma}
          />
          <CardContent sx={{ pt: 0, pb: 0, pl: 1, pr: 1 }}>
            <Grid container spacing={1}>
              {atitudes && atitudes.length > 0 && (
                <Grid item sm={12} md={legendas ? 6 : undefined}>
                  <AccordionAtitudeComponent atitudes={atitudes} />
                </Grid>
              )}
              {legendas && legendas.length > 0 && (
                <Grid item sm={12} md={atitudes ? 6 : undefined}>
                  <AccordionLegendaComponent legendas={legendas} />
                </Grid>
              )}
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ pt: 2 }}>
                <PerGruComponent
                  idsGrupos={[]}
                  tree={tree}
                  tipoOrdem={avaliacao.questionario.tipoOrdem}
                  qtdeEtapas={qtdeEtapas}
                  legendas={legendas}
                  atitudes={atitudes}
                  nomeGrupo="Perguntas"
                  nivel={1}
                  nomeEtapa={sistemaAvaliacao.nomeEtapa || "Bimestre"}
                  etapaAtual={avaliacao.bimestre}
                  idAvaliacao={avaliacao.id}
                  setResposta={setResposta}
                />
              </Box>
              <Box sx={{ textAlign: "right", pt: 1 }}>
                <Button
                  color="secondary"
                  sx={{ mr: 1 }}
                  onClick={handleCancelar}
                >
                  Cancelar
                </Button>
                <LoadingButton
                  variant="outlined"
                  onClick={handleSubmit}
                  loading={saving}
                >
                  Salvar
                </LoadingButton>
              </Box>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};
